import { GUID, TypedEvent } from "@faro-lotv/foundation";
import {
  BackgroundTaskState,
  CoreApiClient,
  CoreMultiFileUploader,
  MultiFileUploadFinalizerCallBack,
  UploadedFileData,
} from "@faro-lotv/service-wires";
import { uniqueId } from "es-toolkit/compat";
import { UploadElementType } from "../point-cloud-file-upload-context/use-upload-element-types";
import {
  RemoveTaskFn,
  UpdateTaskFn,
  UploadFailedCallback,
  UploadUpdatedCallback,
} from "./upload-manager";

/**
 * Callback type reporting that the multi-upload has completed successfully
 *
 * @param id is the ID the multi-upload task has in the store.
 * @param uploadedFilesData is an array containing the data of each uploaded file
 */
export type MultiUploadCompletedCallback = (
  id: GUID,
  uploadedFilesData: UploadedFileData[],
) => void;

/**
 * An object to manage a specific CoreMultiFileUploader object and to connect it to the
 * object managing all multi-file uploads
 */
class MultiFileUploadController {
  uploader: CoreMultiFileUploader;
  abortController = new AbortController();

  uploadCompleted = new TypedEvent<{
    id: GUID;
    uploadedFilesData: UploadedFileData[];
  }>();

  uploadFailed = new TypedEvent<{ id: GUID; error: Error }>();

  uploadUpdated = new TypedEvent<{
    id: GUID;
    progress: number;
    expectedEnd: number;
  }>();

  constructor(
    public id: GUID,
    files: File[],
    projectId: string,
    coreApi: CoreApiClient,
    projectToken: string,
  ) {
    this.uploader = new CoreMultiFileUploader(
      files,
      projectId,
      coreApi,
      projectToken,
    );
    this.uploader.uploadCompleted.on((uploadedFilesData) => {
      this.uploadCompleted.emit({
        id: this.id,
        uploadedFilesData,
      });
    });
    this.uploader.uploadFailed.on((error: Error) => {
      this.uploadFailed.emit({ id: this.id, error });
    });
    this.uploader.progressChanged.on(({ percentage, expectedEnd }) => {
      this.uploadUpdated.emit({
        id: this.id,
        progress: percentage,
        expectedEnd,
      });
    });
  }
}

/**
 *  Function used to notify an upload started to the React context to update the store
 *
 * @param id is the id of the task in the store
 * @param projectId is the id of the project
 * @param uploadElementType is the type of element being uploaded
 * @param files is the handles to the files to upload
 * @param silent true if progress toast notifications should be hidden
 * @param iElementId is an optional id to associate the upload task with
 */
type StartMultiFileUploadFn = (
  id: GUID,
  projectId: string,
  uploadElementType: UploadElementType,
  files: File[],
  silent: boolean,
  iElementId?: GUID,
) => void;

export type MultiFileUploadParams = {
  /** The files to upload */
  files: File[];

  /** The project to add the file to */
  projectId: GUID;

  /** The element the data is uploaded for. Used to associate the progress task with. */
  iElementId?: GUID;

  /** Whether progress toast notifications should be hidden */
  silent?: boolean;

  /** Core API client to perform the upload */
  coreApiClient: CoreApiClient;

  /** The bearer token for authenticated requests to projectApi */
  projectToken: string;

  /** Callback called by the multi-file upload to finalize the upload. If it fails the upload is considered failed */
  finalizer?: MultiFileUploadFinalizerCallBack;

  /** The type of element being uploaded */
  uploadElementType: UploadElementType;

  /**
   * Function called when a given file upload completes successfully.
   * This is executed after the finalizer.
   */
  onUploadCompleted?: MultiUploadCompletedCallback;

  /** Function called when a given file upload fails */
  onUploadFailed?: UploadFailedCallback;

  /** Function called when the progress of a given upload changes */
  onUploadUpdated?: UploadUpdatedCallback;
};

/**
 * The interface of the object contained in the FileUploadContext
 *
 * WARNING: this type should not be used directly, it is just exported
 * as an implementation detail to realize the FileUploadContextProvider,
 * and the hooks useMultiFileUploader and useCancelMultiFileUpload.
 */
export interface MultiFileUploadManagerInterface {
  /**
   * Starts multiple file uploads and adds them as one single task to the managed uploads.
   */
  startMultiFileUpload(params: MultiFileUploadParams): Promise<void>;

  /**
   * @returns Whether the task existed and was correctly canceled, i.e. was in progress.
   * @param id Id of the task to be canceled
   */
  cancelMultiFileUpload(id: GUID): boolean;
}

/**
 * A manager that stores all ongoing multi-file uploads.
 *
 * WARNING: this type should not be used directly, it is just exported
 * as an implementation detail to realize the FileUploadContextProvider,
 * and the hooks useMultiFileUploader and useCancelMultiFileUpload.
 */
export class MultiFileUploadManager implements MultiFileUploadManagerInterface {
  #uploads = new Map<GUID, MultiFileUploadController>();

  /**
   * @param startTaskInStore Function to start a background task in the store
   * @param updateTaskInStore Function to update the task in the store corresponding to the upload
   * @param removeTaskInStore Function to remove a task from the store.
   */
  constructor(
    public startTaskInStore: StartMultiFileUploadFn,
    public updateTaskInStore: UpdateTaskFn,
    public removeTaskInStore: RemoveTaskFn,
  ) {
    this.uploadCompleted = this.uploadCompleted.bind(this);
    this.uploadFailed = this.uploadFailed.bind(this);
    this.uploadUpdated = this.uploadUpdated.bind(this);
  }

  /**
   *
   * @param id ID of the upload to remove
   */
  #removeUpload(id: GUID): void {
    const uploadController = this.#uploads.get(id);
    if (!uploadController) return;
    uploadController.uploadCompleted.off(this.uploadCompleted);
    uploadController.uploadFailed.off(this.uploadFailed);
    uploadController.uploadUpdated.off(this.uploadUpdated);
    this.#uploads.delete(id);
  }

  /**
   * Called when a multi-file upload completes successfully.
   *
   * @param arg the arguments
   * @param arg.id ID of the upload that completed
   * @param arg.uploadedFilesData Array of data for each uploaded file.
   */
  private uploadCompleted(arg: {
    id: GUID;
    uploadedFilesData: UploadedFileData[];
  }): void {
    this.updateTaskInStore(arg.id, { state: BackgroundTaskState.succeeded });
    this.#removeUpload(arg.id);
  }

  /**
   *
   * @param arg the arguments
   * @param arg.id ID of the upload that failed
   * @param arg.error Error thrown that made the upload to fail.
   */
  private uploadFailed(arg: { id: GUID; error: Error }): void {
    this.updateTaskInStore(arg.id, { state: BackgroundTaskState.failed });
    this.#removeUpload(arg.id);
  }

  /**
   *
   * @param arg the arguments
   * @param arg.id ID of the upload that failed
   * @param arg.progress Current progress of the given upload, from 0 to 100
   * @param arg.expectedEnd Expected end timestamp of this task
   */
  private uploadUpdated(arg: {
    id: GUID;
    progress: number;
    expectedEnd: number;
  }): void {
    this.updateTaskInStore(arg.id, {
      progress: arg.progress,
      state: BackgroundTaskState.started,
      expectedEnd: arg.expectedEnd,
    });
  }

  /**
   * Starts a multi-file upload and adds it to the managed uploads.
   *
   * @returns A promise that resolves when the upload has been started.
   */
  startMultiFileUpload({
    projectId,
    files,
    uploadElementType,
    iElementId,
    silent = true,
    coreApiClient,
    projectToken,
    finalizer,
    onUploadCompleted,
    onUploadFailed,
    onUploadUpdated,
  }: MultiFileUploadParams): Promise<void> {
    const id = uniqueId();
    const controller = new MultiFileUploadController(
      id,
      files,
      projectId,
      coreApiClient,
      projectToken,
    );
    if (onUploadCompleted) {
      controller.uploadCompleted.on(
        (arg: { id: GUID; uploadedFilesData: UploadedFileData[] }) =>
          onUploadCompleted(arg.id, arg.uploadedFilesData),
      );
    }
    if (onUploadFailed) {
      controller.uploadFailed.on((arg: { id: GUID; error: Error }) =>
        onUploadFailed(arg.id, arg.error),
      );
    }
    if (onUploadUpdated) {
      controller.uploadUpdated.on((arg: { id: GUID; progress: number }) =>
        onUploadUpdated(arg.id, arg.progress),
      );
    }

    controller.uploadCompleted.on(this.uploadCompleted);
    controller.uploadFailed.on(this.uploadFailed);
    controller.uploadUpdated.on(this.uploadUpdated);
    this.#uploads.set(id, controller);
    // Update the store with the new task
    this.startTaskInStore(
      id,
      projectId,
      uploadElementType,
      files,
      silent,
      iElementId,
    );
    // Start the actual upload
    return controller.uploader.doUpload(
      controller.abortController.signal,
      finalizer,
    );
  }

  /**
   * Cancels a file upload
   *
   * @param id The ID of the corresponding background task in the store
   * @returns Whether the upload existed and was in progress, therefore canceled correctly.
   */
  cancelMultiFileUpload(id: GUID): boolean {
    const controller = this.#uploads.get(id);
    if (
      !controller ||
      controller.uploader.state !== BackgroundTaskState.started
    ) {
      return false;
    }
    controller.abortController.abort();
    this.updateTaskInStore(id, { state: BackgroundTaskState.aborted });
    this.#uploads.delete(id);
    return true;
  }

  /**
   * Removes a file upload from the managed uploads
   *
   * @param id the ID of the upload
   * @returns whether the removal was successful or whether the upload was still in progress.
   */
  removeFileUpload(id: GUID): boolean {
    const controller = this.#uploads.get(id);
    if (!controller) return false;
    if (controller.uploader.state === BackgroundTaskState.started) return false;
    this.#removeUpload(id);
    this.removeTaskInStore(id);
    return true;
  }
}
